import React, { useCallback, useEffect, useState } from "react";
import { Waypoints, Plus, RefreshCw, ShieldAlert, ShieldCheck, Trash2, Power } from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import Layout from "@/components/Layout";
import ConfirmAction from "@/components/ConfirmAction";
import { Card, Button, Chip, Field, Input, Select, Skeleton, EmptyState, Toast } from "@/ui";
import { manilaDateTime } from "@/lib/format";

const STATUS_TONE = { connected: "good", changed: "warn", unreachable: "bad", new: "neutral", not_configured: "neutral" };
const STATUS_LABEL = { connected: "connected", changed: "tools changed", unreachable: "not reachable", new: "new", not_configured: "not set up" };

// Platform Owner: the servers Reto connects to as a CLIENT. Reto can use their
// tools. Adding a server is done here — never in code. When a server changes its
// tools, they are blocked until someone approves the change.
export default function ToolConnections() {
  const [conns, setConns] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [busy, setBusy] = useState("");
  const [remove, setRemove] = useState(null);
  const [form, setForm] = useState({ name: "", url: "", authType: "none", token: "" });

  const load = useCallback(() => {
    setLoading(true);
    api.get("/tools/connections")
      .then((r) => setConns(r.data.connections || []))
      .catch((e) => setError(friendlyError(e)))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 3000); };

  const add = async () => {
    setBusy("add"); setError("");
    try {
      const body = { name: form.name, url: form.url, authType: form.authType,
        token: form.authType === "bearer" ? form.token : null, enabled: true };
      await api.post("/tools/connections", body);
      setForm({ name: "", url: "", authType: "none", token: "" });
      flash("Connection added"); load();
    } catch (e) { setError(friendlyError(e)); }
    finally { setBusy(""); }
  };

  const act = async (id, path, msg) => {
    setBusy(id); setError("");
    try { await api.post(`/tools/connections/${id}/${path}`, path === "enable" ? { enabled: true } : {}); flash(msg); load(); }
    catch (e) { setError(friendlyError(e)); }
    finally { setBusy(""); }
  };

  const toggle = async (c) => {
    setBusy(c.id); setError("");
    try { await api.post(`/tools/connections/${c.id}/enable`, { enabled: !c.enabled }); load(); }
    catch (e) { setError(friendlyError(e)); }
    finally { setBusy(""); }
  };

  const runRemove = async () => {
    setBusy(remove.id); setError("");
    try { await api.post(`/tools/connections/${remove.id}/remove`, {}); flash("Connection removed"); load(); }
    catch (e) { setError(friendlyError(e)); }
    finally { setBusy(""); setRemove(null); }
  };

  return (
    <Layout>
      <div className="row-between" style={{ marginBottom: 16 }}>
        <h1 className="t-h1">tool connections</h1>
        <Button data-testid="refresh-all-btn" onClick={load}><RefreshCw size={16} strokeWidth={1.75} />reload</Button>
      </div>
      <p className="muted" style={{ marginBottom: 20, maxWidth: 680 }}>
        Servers Reto connects to so it can use their tools — BloomConnect first, a courier or accounting app later.
        Anything a tool sends back is treated as plain information, never as an instruction.
      </p>

      {error ? <div className="err-text" data-testid="tools-error" style={{ marginBottom: 12 }}>{error}</div> : null}

      <Card data-testid="add-connection-card" style={{ marginBottom: 16 }}>
        <div className="row" style={{ gap: 10, marginBottom: 12 }}>
          <span className="ph-icon" style={{ margin: 0 }}><Plus size={18} strokeWidth={1.75} /></span>
          <h2 className="t-h2">add a server</h2>
        </div>
        <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
          <Field label="name"><Input data-testid="conn-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Courier" /></Field>
          <Field label="server address (MCP url)"><Input data-testid="conn-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…/mcp" style={{ minWidth: 280 }} /></Field>
          <Field label="sign in">
            <Select data-testid="conn-auth" value={form.authType} onChange={(e) => setForm({ ...form, authType: e.target.value })}>
              <option value="none">none</option>
              <option value="bearer">bearer token</option>
            </Select>
          </Field>
          {form.authType === "bearer" ? (
            <Field label="token"><Input data-testid="conn-token" type="password" value={form.token} onChange={(e) => setForm({ ...form, token: e.target.value })} placeholder="paste token" /></Field>
          ) : null}
          <Button variant="primary" data-testid="conn-add-btn" loading={busy === "add"} disabled={!form.name.trim() || !form.url.trim()} onClick={add}>
            <Plus size={16} strokeWidth={1.75} />add
          </Button>
        </div>
      </Card>

      {loading || conns === null ? (
        <div className="stack"><Skeleton variant="card" /><Skeleton variant="card" /></div>
      ) : conns.length === 0 ? (
        <EmptyState icon={<Waypoints size={20} strokeWidth={1.75} />} title="no connections yet — add one above" />
      ) : (
        <div className="stack" style={{ gap: 16 }}>
          {conns.map((c) => (
            <Card key={c.id} data-testid={`connection-${c.id}`}>
              <div className="row-between" style={{ marginBottom: 8 }}>
                <div className="row" style={{ gap: 10 }}>
                  <span className="ph-icon" style={{ margin: 0 }}><Waypoints size={18} strokeWidth={1.75} /></span>
                  <h2 className="t-h2">{c.name}</h2>
                  {c.managed ? <Chip tone="neutral">built-in</Chip> : null}
                </div>
                <Chip tone={STATUS_TONE[c.status] || "neutral"} dot data-testid={`conn-status-${c.id}`}>{STATUS_LABEL[c.status] || c.status}</Chip>
              </div>

              <div className="row-between"><span className="muted t-small">address</span><span className="mono t-small" style={{ wordBreak: "break-all" }}>{c.url || "not set"}</span></div>
              <div className="row-between"><span className="muted t-small">tools</span><span className="num t-small">{c.toolCount}</span></div>
              <div className="row-between"><span className="muted t-small">tool-list hash</span><span className="mono t-small" data-testid={`conn-hash-${c.id}`}>{c.toolsHash ? c.toolsHash.slice(0, 12) + "\u2026" : "\u2014"}</span></div>
              <div className="row-between"><span className="muted t-small">last reachable</span><span className="muted-3 t-small">{c.lastReachableAt ? manilaDateTime(c.lastReachableAt) : "not yet"}</span></div>
              {c.lastError ? <p className="err-text t-small" style={{ marginTop: 6, wordBreak: "break-word" }}>{c.lastError}</p> : null}

              {c.toolsChanged ? (
                <div className="offline-banner" data-testid={`conn-changed-${c.id}`} style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center" }}>
                  <ShieldAlert size={16} strokeWidth={1.75} />
                  This server changed its tools. They are blocked until you approve the change.
                </div>
              ) : null}

              {c.tools.length ? (
                <div className="row" style={{ gap: 6, flexWrap: "wrap", marginTop: 12 }} data-testid={`conn-tools-${c.id}`}>
                  {c.tools.map((t) => <Chip key={t.name} tone="neutral">{t.name}</Chip>)}
                </div>
              ) : null}

              <div className="divider" style={{ margin: "14px 0" }} />
              <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                <Button size="sm" data-testid={`conn-refresh-${c.id}`} loading={busy === c.id} onClick={() => act(c.id, "refresh", "Checked for tool changes")}>
                  <RefreshCw size={14} strokeWidth={1.75} />check tools
                </Button>
                {c.toolsChanged ? (
                  <Button size="sm" variant="primary" data-testid={`conn-approve-${c.id}`} onClick={() => act(c.id, "approve", "Tool change approved")}>
                    <ShieldCheck size={14} strokeWidth={1.75} />approve change
                  </Button>
                ) : null}
                <Button size="sm" data-testid={`conn-toggle-${c.id}`} onClick={() => toggle(c)}>
                  <Power size={14} strokeWidth={1.75} />{c.enabled ? "switch off" : "switch on"}
                </Button>
                {!c.managed ? (
                  <Button size="sm" variant="danger" data-testid={`conn-remove-${c.id}`} onClick={() => setRemove(c)}>
                    <Trash2 size={14} strokeWidth={1.75} />remove
                  </Button>
                ) : null}
              </div>
            </Card>
          ))}
        </div>
      )}

      <ConfirmAction
        open={!!remove}
        title="Remove this connection?"
        preview={[`Reto will stop using tools from "${remove?.name || ""}".`, "You can add it again later."]}
        confirmLabel="Yes, remove it" busy={!!busy} error={error}
        onConfirm={runRemove} onCancel={() => setRemove(null)}
      />

      {toast ? <div className="toast-wrap"><Toast tone="good" data-testid="tools-toast">{toast}</Toast></div> : null}
    </Layout>
  );
}
